// Global top bar rendered on every authenticated page: compact machine
// controls, dark-mode toggle, the system-notifications bell (with click-to-
// dismiss persistence and CoPilot handoff), and logout.
import {
  Box,
  IconButton,
  Badge,
  Popover,
  Typography,
  Divider,
  Button,
  Tooltip,
  useTheme,
} from "@mui/material";
import { useContext, useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ColorModeContext, tokens } from "../../theme";
import { useAuth } from "../../auth/AuthContext";
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import NotificationsOutlinedIcon from "@mui/icons-material/NotificationsOutlined";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import Popup from "../../components/Popup";
import MachineControls from "../../components/MachineControls";
import api from "../../services/api";

const WARNINGS_POLL_MS = 30_000;

const SEVERITY_META = {
  error: { color: "redAccent", Icon: ErrorOutlineOutlinedIcon },
  warning: { color: "orangeAccent", Icon: WarningAmberOutlinedIcon },
  info: { color: "tealAccent", Icon: InfoOutlinedIcon },
};

const Topbar = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const colorMode = useContext(ColorModeContext);
  const isDark = theme.palette.mode === "dark";
  const navigate = useNavigate();

  const authContext = useAuth();
  const logout = authContext?.logout || (() => {});
  const isAuthenticated = !!authContext?.user;

  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);

  const [allWarnings, setAllWarnings] = useState([]);
  const [warningsAnchor, setWarningsAnchor] = useState(null);
  const bellRef = useRef(null);

  // Client-side dismiss persistence: Map of id → dismissedAtMs in localStorage
  // with a 24h TTL. CRITICAL: never re-show on `ts` change or on server-list
  // membership — both made dismissed warnings reappear.
  // See docs/design-decisions.md#ui-topbar-dismiss-ttl
  const DISMISS_TTL_MS = 24 * 60 * 60 * 1000;
  const [dismissed, setDismissed] = useState(() => {
    try {
      const raw = localStorage.getItem("topbar_dismissed_warnings");
      const parsed = raw ? JSON.parse(raw) : null;
      const now = Date.now();
      // New shape: { id: dismissedAtMs }
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        const m = new Map();
        for (const [id, at] of Object.entries(parsed)) {
          const ts = Number(at);
          if (Number.isFinite(ts) && now - ts < DISMISS_TTL_MS) m.set(id, ts);
        }
        return m;
      }
      // Legacy flat-array shape: treat as "dismissed just now" so nothing
      // re-appears immediately after the upgrade.
      if (Array.isArray(parsed)) {
        const m = new Map();
        for (const id of parsed) m.set(id, now);
        return m;
      }
      return new Map();
    } catch (_) { return new Map(); }
  });
  const persistDismissed = useCallback((next) => {
    try {
      // Serialise as a plain { id: ts } object — JSON has no Map.
      const obj = {};
      for (const [id, at] of next) obj[id] = at;
      localStorage.setItem("topbar_dismissed_warnings", JSON.stringify(obj));
    } catch (_) {}
  }, []);

  const fetchWarnings = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const { data } = await api.get("/system/warnings");
      setAllWarnings(Array.isArray(data?.warnings) ? data.warnings : []);
    } catch (e) {
      // swallow — bell just shows zero. Logging here would spam the
      // console on every poll if Node is briefly unreachable.
    }
  }, [isAuthenticated]);

  useEffect(() => {
    fetchWarnings();
    const t = setInterval(fetchWarnings, WARNINGS_POLL_MS);
    return () => clearInterval(t);
  }, [fetchWarnings]);

  const dismissWarning = useCallback((w) => {
    setDismissed((prev) => {
      const next = new Map(prev);
      next.set(w.id, Date.now());
      persistDismissed(next);
      return next;
    });
  }, [persistDismissed]);

  // Bulk-dismiss what is visible right now (idempotent). Future watchdog
  // bulletins still appear — each occurrence has a unique id.
  const dismissAllWarnings = useCallback(() => {
    setDismissed((prev) => {
      if (allWarnings.length === 0) return prev;
      const next = new Map(prev);
      const now = Date.now();
      for (const w of allWarnings) next.set(w.id, now);
      persistDismissed(next);
      return next;
    });
  }, [allWarnings, persistDismissed]);

  // Visible = (server warnings) − (dismissed ids that haven't aged out).
  const warnings = allWarnings.filter((w) => !dismissed.has(w.id));

  // TTL-based GC only — membership-based GC is the bug the decision record
  // describes. See docs/design-decisions.md#ui-topbar-dismiss-ttl
  useEffect(() => {
    const tick = () => {
      setDismissed((prev) => {
        if (prev.size === 0) return prev;
        const now = Date.now();
        const next = new Map();
        let changed = false;
        for (const [id, at] of prev) {
          if (now - at < DISMISS_TTL_MS) next.set(id, at);
          else changed = true;
        }
        if (!changed) return prev;
        persistDismissed(next);
        return next;
      });
    };
    tick(); // also prune on mount
    const t = setInterval(tick, 60 * 60 * 1000); // hourly
    return () => clearInterval(t);
  }, [persistDismissed, DISMISS_TTL_MS]);

  const openWarnings = (e) => {
    setWarningsAnchor(e.currentTarget);
    fetchWarnings();
  };
  const closeWarnings = () => setWarningsAnchor(null);

  const highestSeverity = warnings.reduce((acc, w) => {
    if (w.severity === "error") return "error";
    if (w.severity === "warning" && acc !== "error") return "warning";
    return acc;
  }, null);

  const badgeColor =
    highestSeverity === "error"
      ? colors.redAccent[500]
      : highestSeverity === "warning"
      ? colors.orangeAccent[500]
      : null;

  return (
    <Box
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      height="140px"
      px="20px"
      py={2}
    >
      <Box></Box>

      <Box display="flex" alignItems="center">
        {/* Machine controls, visible from every authenticated page (hidden on
            login where the actions would 401 anyway). */}
        {isAuthenticated && (
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              mr: 4,
            }}
          >
            <MachineControls
              layout="topbar"
              showTitle={false}
              styles={{
                buttonHeight: "32px",
                buttonFontSize: "0.78rem",
                buttonGap: 1,
                stateBadge: {
                  // State pill kept small so it doesn't dwarf the icons;
                  // height sizes the pill, fontSize sizes only the text.
                  height: "16px",
                  px: 0.85,
                  borderRadius: 0.75,
                  fontSize: "0.50rem",
                },
              }}
            />
          </Box>
        )}

        <IconButton
          onClick={colorMode?.toggleColorMode || (() => {})}
          sx={{ color: colors.primary[800] }}
        >
          {theme.palette.mode === "light" ? (
            <DarkModeOutlinedIcon />
          ) : (
            <LightModeOutlinedIcon />
          )}
        </IconButton>

        <IconButton
          ref={bellRef}
          onClick={openWarnings}
          sx={{ color: colors.primary[800] }}
          title={
            warnings.length > 0
              ? `${warnings.length} system warning${warnings.length === 1 ? "" : "s"}`
              : "No system warnings"
          }
        >
          <Badge
            invisible={warnings.length === 0}
            overlap="circular"
            badgeContent={warnings.length || null}
            sx={{
              "& .MuiBadge-badge": {
                backgroundColor: badgeColor || colors.tealAccent[500],
                color: "#fff",
                fontWeight: 700,
                minWidth: 18,
                height: 18,
              },
            }}
          >
            <NotificationsOutlinedIcon />
          </Badge>
        </IconButton>

        <Popover
          open={!!warningsAnchor}
          anchorEl={warningsAnchor}
          onClose={closeWarnings}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          slotProps={{
            paper: {
              sx: {
                mt: 1,
                width: 380,
                maxHeight: 480,
                overflow: "auto",
                bgcolor: isDark ? colors.primary[100] : "#fff",
                border: `1px solid ${isDark ? colors.primary[400] : colors.grey[300]}`,
                borderRadius: 1.5,
                boxShadow: 8,
              },
            },
          }}
        >
          <Box px={2} py={1.5} display="flex" alignItems="center" justifyContent="space-between" gap={1}>
            <Box display="flex" alignItems="baseline" gap={1.25} minWidth={0}>
              <Typography variant="subtitle1" fontWeight={700} sx={{ color: isDark ? colors.grey[900] : colors.grey[900] }}>
                System notifications
              </Typography>
              <Typography variant="caption" sx={{ color: isDark ? colors.grey[500] : colors.grey[500] }}>
                {warnings.length === 0
                  ? "All clear"
                  : `${warnings.length} active`}
              </Typography>
            </Box>
            {warnings.length > 0 && (
              <Button
                size="small"
                onClick={dismissAllWarnings}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  color: colors.tealAccent[500],
                  minWidth: "auto",
                  px: 1,
                  "&:hover": {
                    backgroundColor: isDark ? colors.primary[300] : colors.grey[100],
                  },
                }}
              >
                Dismiss all
              </Button>
            )}
          </Box>
          <Divider sx={{ borderColor: isDark ? colors.primary[400] : colors.grey[200] }} />

          {warnings.length === 0 ? (
            <Box px={2} py={3} textAlign="center">
              <Typography variant="body2" sx={{ color: colors.grey[500] }}>
                No active warnings.
              </Typography>
            </Box>
          ) : (
            warnings.map((w) => {
              const meta = SEVERITY_META[w.severity] || SEVERITY_META.info;
              const accent = colors[meta.color]?.[500] || colors.tealAccent[500];
              const Icon = meta.Icon;
              const clickable = !!w.link;
              // Tooltip spells out exactly what the click will do so it's
              // never a surprise.
              const openTooltip = w.coPilotQuestion
                ? `Open CoPilot and ask: "${w.coPilotQuestion}"`
                : "Open CoPilot for this alert";
              // Same sessionStorage stash + copilot:ask event the CoPilotFeed
              // uses, so the chat behaves identically from both entry points.
              const openInCoPilot = () => {
                dismissWarning(w);
                if (!clickable) return;
                closeWarnings();
                const question = w.coPilotQuestion;
                if (question) {
                  const detail = { question, bulletinId: w.coPilotBulletinId };
                  const onCoPilotPage =
                    typeof window !== "undefined" &&
                    window.location.pathname.toLowerCase().startsWith("/copilot");
                  if (onCoPilotPage) {
                    window.dispatchEvent(new CustomEvent("copilot:ask", { detail }));
                    return;
                  }
                  try {
                    sessionStorage.setItem(
                      "copilot:pendingQuestion",
                      JSON.stringify({ ...detail, ts: Date.now() }),
                    );
                  } catch (e) {
                    console.warn("[Topbar] sessionStorage write failed:", e);
                  }
                }
                navigate(w.link);
              };
              return (
                <Box
                  key={w.id}
                  px={2}
                  py={1.5}
                  onClick={openInCoPilot}
                  sx={{
                    borderLeft: `3px solid ${accent}`,
                    cursor: "pointer",
                    "&:hover": {
                      backgroundColor: isDark ? colors.primary[300] : colors.grey[100],
                    },
                    "&:not(:last-of-type)": {
                      borderBottom: `1px solid ${isDark ? colors.primary[400] : colors.grey[200]}`,
                    },
                  }}
                >
                  <Box display="flex" alignItems="flex-start" gap={1.25}>
                    <Icon sx={{ color: accent, mt: "2px" }} fontSize="small" />
                    <Box flex={1} minWidth={0}>
                      <Typography
                        variant="body2"
                        fontWeight={700}
                        sx={{ color: isDark ? colors.grey[900] : colors.grey[900], mb: 0.25 }}
                      >
                        {w.title}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: isDark ? colors.grey[900] : colors.grey[700],
                          lineHeight: 1.4,
                        }}
                      >
                        {w.message}
                      </Typography>
                      {w.hint && (
                        <Typography
                          variant="caption"
                          sx={{
                            display: "block",
                            mt: 0.75,
                            color: isDark ? colors.grey[600] : colors.grey[600],
                            fontStyle: "italic",
                            // Preserve the newline between the backend's
                            // "Why: …" and "Recommendation: …" lines.
                            whiteSpace: "pre-line",
                          }}
                        >
                          {w.hint}
                        </Typography>
                      )}
                      {clickable ? (
                        <Tooltip title={openTooltip} placement="top" arrow>
                          <Typography
                            variant="caption"
                            sx={{
                              display: "inline-block",
                              mt: 0.5,
                              color: accent,
                              fontWeight: 600,
                              cursor: "pointer",
                            }}
                          >
                            Open in CoPilot →
                          </Typography>
                        </Tooltip>
                      ) : (
                        <Typography
                          variant="caption"
                          sx={{
                            display: "block",
                            mt: 0.5,
                            color: accent,
                            fontWeight: 600,
                          }}
                        >
                          Click to dismiss
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Box>
              );
            })
          )}
        </Popover>

        <IconButton
          onClick={() => setLogoutDialogOpen(true)}
          sx={{ color: colors.primary[800], mr: -1 }}
          title="Logout"
        >
          <LogoutOutlinedIcon />
        </IconButton>
      </Box>

      <Popup
        open={logoutDialogOpen}
        onClose={() => setLogoutDialogOpen(false)}
        onConfirm={() => {
          setLogoutDialogOpen(false);
          logout();
        }}
        title="Confirm Logout"
        message="Are you sure you want to log out? You will be returned to the login screen."
        confirmLabel="Logout"
        confirmColor="error"
      />
    </Box>
  );
};

export default Topbar;
